import {useTranslation} from "react-i18next";
import {useHistory,useParams,useLocation} from "react-router-dom";
import {useGetOrganizationAwardPositions,useGetOrganizationSettings} from "../../services/ContentManager";
import NTMXGrid,{timestampFormatterWithTimezone} from "../../components/NTMXGrid";
import RenderCellAvatar from "../../components/cellRender/RenderCellAvatar";
import React,{useEffect,useState} from "react";
import {useAwardTypeNames} from "../../constants/awardType";
import RenderCell from "../../components/cellRender/RenderCell";
import StartIconButton from "../../components/buttons/StartIconButton";
import { Grid, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Divider, CircularProgress } from "@mui/material";
import AwardPositionManager from "./AwardPosition/AwardPositionManager";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RenderCoordinates from "../../components/cellRender/RenderCoordinates";
import ConfirmIconButton from "../../components/buttons/ConfirmIconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {AWARD_POSITIONS,AWARDS,deleteElem,getErrorMessage,ORGANIZATIONS,POSITIONS} from "../../services/Client";
import {useSnackbar} from "notistack";
import {useQueryClient} from "react-query";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {SettingsTooltip} from "../../components/forms/CustomTooltip";
import dayjs from "dayjs";
import { Button } from "@mui/material/index";
import {firebaseAuth} from "../../firebase";

// Costruisce i valori per un nuovo premio a partire da uno esistente:
// rimuove id, campi di audit e campi legati alla singola assegnazione/visualizzazione.
const buildCloneSource = (award) => {
    const {
        id, createdDate, lastModifiedDate, createdBy, lastModifiedBy,
        timestamp, uid, username, couponStatus, organizationName, timezone,
        ...clone
    } = award;
    return clone;
}

export default function AwardPositions() {
    const API_URL = process.env.REACT_APP_API_URL;

    const {t} = useTranslation();
    const {id, filter, actionId} = useParams();
    const location = useLocation();
    let {awardPositions, status} = useGetOrganizationAwardPositions(id)
    let history = useHistory();
    const typeLabels = useAwardTypeNames()
    const {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();

    let [timezone, setTimezone] = useState();
    let {settings = []} = useGetOrganizationSettings(id);
    useEffect(() => {
        let setting = settings.find(o => o.organizationSetting === "timezone")
        if (setting) setTimezone(setting.value || setting.defaultValue)
    }, [settings])
    const [openImportLegend, setOpenImportLegend] = useState(false);
    const [importLoading, setImportLoading] = useState(false);

    if(actionId) {
        let source;
        if(actionId === 'create') {
            // duplicazione: l'id del premio da clonare arriva come query param ?from=<id>
            const fromId = new URLSearchParams(location.search).get('from');
            const original = fromId && awardPositions.find(a => a.id == fromId);
            if(original) source = buildCloneSource(original);
        } else {
            source = awardPositions.find(a => a.id == actionId);
        }
        return <AwardPositionManager award={source} timezone={timezone}/>
    }

    const defaultColumns = [
        {
            headerName: t('logo'),
            field: 'imageUrl',
            width: 130,
            renderCell: (params) => <RenderCellAvatar params={params}/>
        },
        {
            headerName : t('name'),
            field : 'name',
            width : 200,
        },
        {
            headerName : t('description'),
            field : 'description',
            width : 200,
        },
        {
            headerName: t('startDate'),
            field: 'startDate_date',
            width: 280,
            //hide: true,
            ...timestampFormatterWithTimezone
        },
        {
            headerName: t('endDate'),
            field: 'endDate_date',
            width: 280,
            //hide: true,
            ...timestampFormatterWithTimezone
        },
        {
            headerName: t('timeSlotStart'),
            field: 'startTime',
            width: 200,
            valueGetter: ({value}) => {
                if(!value && value !== 0) return ""
                let temp = dayjs.tz("00:00", "HH:mm", "utc").add(dayjs.duration(value * 1000))
                return `${temp.tz(timezone).format("HH:mm")} (${timezone})`
            }
        },
        {
            headerName: t('timeSlotEnd'),
            field: 'endTime',
            width: 200,
            valueGetter: ({value}) => {
                if(!value && value !== 0) return ""
                let temp = dayjs.tz("00:00", "HH:mm", "utc").add(dayjs.duration(value * 1000))
                return `${temp.tz(timezone).format("HH:mm")}  (${timezone})`
            }
        },
        {
            headerName: t('value'),
            field: 'value',
            width: 200
        },
        {
            headerName: t('type'),
            field: 'type',
            width: 200,
            valueGetter: (params) => params.value || params.value === 0 ? typeLabels[params.value] : ""
        },
        {
            headerName: t('address'),
            field: 'address',
            width: 220,
        },
        {
            headerName: t('houseNumber'),
            field: 'number',
            width: 220,
        },
        {
            headerName: t('city'),
            field: 'cityName',
            width: 220,
        },
        {
            headerName: t('coordinates'),
            field: 'coordinates',
            width: 300,
            valueGetter: (params) =>  (params.row.latitude && params.row.longitude) ? `${params.row.latitude || ''} - ${params.row.longitude || ''}` : "",
            renderCell: (params) => <RenderCoordinates lat={params.row.latitude} lng={params.row.longitude} radius={params.row.radius * 1000}/>
        },
        {
            headerName: t('tolerance'),
            field: 'radius',
            width: 220,
            valueGetter: ({value}) => value ? value * 1000 : "",
            renderCell: (params) => {
                return <RenderCell params={params} measureUnit="m"/>
            }
        },
        {
            headerName: t('sessionMinDistance'),
            field: 'sessionMinDistance',
            width: 220,
            valueGetter: ({value}) => value ? value * 1000 : "",
            renderCell: (params) => {
                return <RenderCell params={params} measureUnit="m"/>
            }
        },
        {
            headerName: t('expirationDays'),
            field: 'expirationDays',
            width: 220,
        },
        {
            headerName: t('winningsAllowed'),
            field: 'winningsAllowed',
            width: 300,
            renderCell: (params) => params.value || "∞"
        },
        {
            headerName: ' ',
            field: 'editDelete',
            width: 150,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            hideable: false,
            renderCell: (params => {
                const expired = params.row.endDate < new Date();
                return <Grid container direction={"row"}>
                    {!expired &&
                        <ConfirmIconButton
                            onConfirm={() => deleteAwardPosition(params.row['id'])}
                            title={t('confirmDeletion')}
                            text={t('requestDefinitiveEliminationAwardPosition') + " \"" + params.row['name']+"\"?"}
                            warningMessage={params.row.startDate < new Date() && t('warningMessageAwardPositionDelete')}
                        >
                            <DeleteIcon/>
                        </ConfirmIconButton>
                    }
                    {!expired &&
                        <IconButton onClick={() => history.push(`/dashboard/${id}/${filter}/${params.row.id}`)} aria-label="edit"><EditIcon/></IconButton>
                    }
                    <SettingsTooltip title={t('clone')}>
                        <IconButton
                            aria-label="duplicate"
                            onClick={() => history.push(`/dashboard/${id}/${filter}/create?from=${params.row.id}`)}
                        >
                            <ContentCopyIcon/>
                        </IconButton>
                    </SettingsTooltip>
                </Grid>
            }),
        }
    ]

    const deleteAwardPosition = (awardId) => {
        enqueueSnackbar(t('saving...'), {variant: "info"});
        deleteElem(`${AWARDS}/${POSITIONS}/${awardId}`)
            .then(() => enqueueSnackbar(t('removed'), {variant: "success"}))
            .catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}))
            .finally(() => queryClient.invalidateQueries([ORGANIZATIONS, {id: id}, AWARD_POSITIONS]));
    }

    const onImportCsvClick = () => {
        setOpenImportLegend(true);
    };

    const onPickAndImport = async () => {
        if (importLoading) return;

        const file = await pickCsvFile();
        if (!file) return; // annullato

        importAwardPositionsCsv(file);
    };

    awardPositions = awardPositions
        .slice()
        .sort((a, b) => b.endDate - a.endDate)
        .map((ap) => ({ ...ap, timezone }));

    const importAwardPositionsCsv = async (file) => {
        if (!file) return;

        setImportLoading(true);
        enqueueSnackbar(t("saving..."), { variant: "info" });

        try {
            const formData = new FormData();
            formData.append("file", file);

            const token = firebaseAuth.currentUser
                ? await firebaseAuth.currentUser.getIdToken(true)
                : null;

            const res = await fetch(`${API_URL}organizations/${id}/import-award-positions-csv`, {
                method: "POST",
                body: formData,
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            const contentType = res.headers.get("content-type") || "";

            // Se backend torna CSV (tipicamente 422) -> scarico file
            if (contentType.includes("text/csv")) {
                const blob = await res.blob();

                // provo a leggere filename da Content-Disposition
                const cd = res.headers.get("content-disposition") || "";
                const match = cd.match(/filename="([^"]+)"/i);
                const filename = match?.[1] || "award_positions_remaining.csv";

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                enqueueSnackbar(
                    "Import completato parzialmente: scaricato CSV con le righe non importate.",
                    { variant: "warning" }
                );

                queryClient.invalidateQueries([ORGANIZATIONS, { id }, AWARD_POSITIONS]);
                return;
            }

            // Caso OK JSON
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `Upload failed (${res.status})`);
            }

            enqueueSnackbar(t("saved"), { variant: "success" });
            queryClient.invalidateQueries([ORGANIZATIONS, { id }, AWARD_POSITIONS]);

        } catch (e) {
            enqueueSnackbar(getErrorMessage(e), { variant: "error" });
        } finally {
            setImportLoading(false);
            setOpenImportLegend(false);
        }
    };

    const pickCsvFile = () =>
        new Promise((resolve) => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".csv,text/csv";
            input.style.display = "none";

            const cleanup = () => {
                input.removeEventListener("change", onChange);
                document.body.removeChild(input);
            };

            const onChange = () => {
                const file = input.files && input.files[0] ? input.files[0] : null;
                cleanup();
                resolve(file);
            };

            input.addEventListener("change", onChange);
            document.body.appendChild(input);
            input.click();
        });

    return (
        <div>
            <NTMXGrid
                key={"organization-award-positions"}
                rows={awardPositions || []}
                columns={defaultColumns}
                loading={status === "loading"}
                title={t("awardPositions")}
                rightButton={
                    <Grid container justifyContent={"flex-end"} alignItems="center" gap={1}>
                        <Button
                            variant="contained"
                            onClick={onImportCsvClick}
                            startIcon={<UploadFileIcon />}
                        >
                            Importa CSV
                        </Button>

                        <StartIconButton
                            onClick={() => history.push(`/dashboard/${id}/${filter}/create`)}
                            title={t("addAward")}
                            startIcon={<AddIcon />}
                        />
                    </Grid>
                }
                initialState={{ pinnedColumns: { right: ["editDelete"] } }}
            />
            <Dialog
                open={openImportLegend}
                onClose={() => setOpenImportLegend(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Importa CSV</DialogTitle>

                <DialogContent dividers>
                    {importLoading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">
                                Caricamento in corso… sto importando il CSV.
                            </Typography>
                        </div>
                    )}
                    <Typography variant="body1" gutterBottom>
                        <b>Formato CSV</b>
                    </Typography>

                    <Typography variant="body2">
                        • Separatore: <b>punto e virgola</b> <br />
                        • <b>name</b>: titolo. <br />
                        • <b>description</b>: descrizione. <br />
                        • <b>address</b>: indirizzo testuale (via, città). <br />
                        • <b>latitude</b> e <b>longitude</b>: in formato <b>decimale</b> (es. 45.70463;-9.62133) oppure <b>DMS</b> (es. 45°42'16.7"N;9°37'16.8"E). Opzionali; se vuote vengono calcolate automaticamente dall'<b>address</b>.<br />
                        • <b>radius</b>: raggio di validità dalle coordinate espresso in <b>chilometri</b>. Opzionale; valore di default <b>0.5 km</b>. <br />
                        • <b>sessionMinDistance</b>: distanza minima in <b>chilometri</b> (es. 1.5). Opzionale; valore di default <b>1 km</b>. <br />
                        • <b>startDate</b> ed <b>endDate</b>: formato <b>AAAA-MM-GG</b> (es. 2025-12-31) <br />
                        • <b>startTime</b> ed <b>endTime</b>: formato <b>HH:mm</b> (es. 09:00); opzionali, se vuoti indicano inizio e fine giornata <br />
                        • <b>timezone</b>: fuso orario di riferimento (es. Europe/Rome). Opzionale; valore di default Europe/Rome. <br />
                        • <b>targetUrl</b>: URL. <br />
                        • <b>visibleOnMap</b>: indica se il premio è visibile sulla mappa. Valori ammessi <b>true</b> / <b>false</b>. Opzionale; default <b>true</b> per iniziative turistiche. <br />
                        • <b>value</b>: valore del premio (es. 10.50). <br />
                        • <b>type</b>: tipologia di premio. Valori ammessi: <b>MONEY</b>, <b>POINTS</b>, <b>SHOP</b>, <b>VIA_MAIL</b>. <br />
                        • <b>imageUrl</b>: URL dell'immagine. <br />

                    </Typography>

                    <Divider style={{ margin: "16px 0" }} />

                    <Typography variant="body1" gutterBottom>
                        <b>Header richiesto</b>
                    </Typography>

                    <div
                        style={{
                            position: "relative",
                            overflowX: "auto",
                            overflowY: "hidden",
                            background: "rgba(0,0,0,0.04)",
                            padding: 12,
                            borderRadius: 8,
                            maxWidth: "100%"
                        }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            style={{ position: "absolute", top: 6 }}
                            disabled={importLoading}
                            onClick={() => {
                                const header =
                                    "name,description,address,latitude,longitude,radius,sessionMinDistance,startDate,endDate,startTime,endTime,timezone,targetUrl,visibleOnMap,value,type,imageUrl,winningsAllowed,expirationDays,error_reason";
                                navigator.clipboard.writeText(header);
                                enqueueSnackbar("Header CSV copiato negli appunti", { variant: "success" });
                            }}
                        >
                            Copia
                        </Button>

                        <Typography
                            variant="body2"
                            component="pre"
                            style={{
                                margin: 0,
                                whiteSpace: "pre",
                                wordBreak: "normal",
                                paddingLeft: 80
                            }}
                        >
                            {"name,description,address,latitude,longitude,radius,sessionMinDistance,startDate,endDate,startTime,endTime,timezone,targetUrl,visibleOnMap,value,type,imageUrl,winningsAllowed,expirationDays,error_reason"}
                        </Typography>
                    </div>

                    <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
                        Se alcune righe falliscono, il sistema scarica automaticamente un CSV con le righe non importate e la motivazione.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenImportLegend(false)} disabled={importLoading}>
                        {t("cancel") || "Annulla"}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={onPickAndImport}
                        startIcon={importLoading ? <CircularProgress size={18} /> : <UploadFileIcon />}
                        disabled={importLoading}
                    >
                        {importLoading ? "Import in corso..." : "Scegli file e importa"}
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}
